<script setup lang="ts">
import {
    computed,
    onMounted,
    onUnmounted,
    ref,
    onUpdated,
} from 'vue';
import { useRoute } from 'vue-router';
import { useQuasar } from 'quasar';
import { storeToRefs } from 'pinia';
import FlightSchoolCourseSelection from '@/components/Navigation/CourseSelection/FlightSchoolCourseSelection.vue';
import FlightSchoolSelection from '@/components/Navigation/CourseSelection/FlightSchoolSelection.vue';
import { useSchoolStore } from '@/stores/school';
import { useUserStore } from '@/stores/user';
import FsumNavButton from '@/components/Navigation/FsumNavButton.vue';
import part61Icon from '@/assets/icons/61icon.svg';
import part141Icon from '@/assets/icons/141icon.svg';
import { type AvailableParts, availableParts } from '@/types/Certificate';
import type FlightSchool from '@/types/FlightSchool';
import type FlightSchoolCourse from '@/types/FlightSchoolCourse';
import FsumNavMenu from '@/components/Navigation/FsumNavMenu.vue';
import { usePartStore } from '@/stores/part';

/**
 * Responsive SideNav Height utilizing Quasar's screen size detection
 */
const $q = useQuasar();
const sideNavClasses = computed<string>(() => {
    const baseClasses = 'column justify-between no-wrap';
    return $q.screen.gt.sm ? `${baseClasses} full-height` : '';
});

const userStore = useUserStore();
const schoolStore = useSchoolStore();
const { school, course } = storeToRefs(schoolStore);
const coursesByPart = computed<{ [key in AvailableParts]: FlightSchoolCourse[] }>(() => (
    {
        141: school.value?.courses.filter((c) => c.certificate.part === 141) || [],
        61: school.value?.courses.filter((c) => c.certificate.part === 61) || [],
    }
));
const route = useRoute();
const partStore = usePartStore();
const currentPart = computed(() => partStore.getPartOrUseDefault(school.value));

const icons: Record<AvailableParts, string> = {
    141: part141Icon,
    61: part61Icon,
};

const schoolHasCoursesInAllParts = computed(() => {
    if (school.value === null) return false;

    return Object.values(coursesByPart.value).every((courses) => courses.length > 0);
});

// Only reached if the school has courses in all parts. Determines the course to be used when parts are switched.
const newRouteCourse = (selectedPart: AvailableParts): FlightSchoolCourse|null => {
    if (school.value === null || !schoolHasCoursesInAllParts.value) {
        return null;
    }

    // If the selected part has the current course acronym, find the course and return it
    if (course.value && course.value.certificate.part !== selectedPart) {
        return coursesByPart.value[selectedPart].find(
            (c) => c.certificate.acronym === course.value?.certificate.acronym,
        ) || coursesByPart.value[selectedPart][0];
    }

    return coursesByPart.value[selectedPart][0];
};

const courseDefaultRoute = computed(
    () => ((route.meta.maintainRouteOnSchoolChange === false) ? 'classOverview' : route.name),
);

/**
 * The height of the navigation container should take up as much space as possible on tablets and mobile devices.
 *
 * Since the course selection uses a scroll area, which needs to take up the remaining space
 * we need to subtract the total height (container height) and the submenu height plus padding
 * to get the height available for the course selection.
 */
const clientHeight = ref('0px');
const headerHeight = 'var(--fsum-header-height)';
const navSubMenuHeight = ref('0px');
const menuPadding = '60px';
const courseSelectionHeight = computed(
    () => `calc(${clientHeight.value} - ${headerHeight} - ${navSubMenuHeight.value} - ${menuPadding})`,
);

const updateHeights = () => {
    const { documentElement } = document;
    const navSubMenu = document.querySelector('.sub-menu');

    if (documentElement) {
        clientHeight.value = `${documentElement.clientHeight}px`;
    }
    if (navSubMenu) {
        navSubMenuHeight.value = `${navSubMenu.clientHeight}px`;
    }
};

window.addEventListener('resize', updateHeights);

onMounted(() => {
    updateHeights();

    // Allows the height to be updated when the route changes
    onUpdated(() => {
        updateHeights();
    });
});

onUnmounted(() => {
    window.removeEventListener('resize', updateHeights);
});
</script>

<template>
    <!-- eslint-disable max-len -->
    <div
        :class="sideNavClasses"
    >
        <div
            role="region"
            aria-label="navigation menu"
            class="nav-container full-width full-height"
        >
            <div class="full-width sub-menu">
                <FsumNavMenu :is-admin-center="false">
                    <template #menu-items>
                        <FsumNavButton
                            v-if="userStore.isGlobalAdmin || userStore.isSchoolOwner"
                            role="menuitem"
                            label="Go to Admin Center"
                            icon="sym_o_admin_panel_settings"
                            class="q-px-sm"
                            dense
                            :to="{ name: 'schoolDirectory' }"
                        />
                        <div
                            v-if="school !== null && schoolHasCoursesInAllParts"
                            class="switch-parts"
                        >
                            <q-separator />
                            <div class="nav-subtitle q-py-md">
                                Switch Parts
                            </div>
                            <div class="part-buttons q-gutter-y-md">
                                <FsumNavButton
                                    v-for="part in availableParts"
                                    :key="part"
                                    role="menuitem"
                                    :label="`Cross-Check ${part}`"
                                    :icon="'img:' + icons[part]"
                                    dense
                                    :icon-right="currentPart === part ? 'check' : ''"
                                    red-indicator-location="none"
                                    border-radius="1px"
                                    :active="currentPart === part"
                                    :to="{
                                        name: courseDefaultRoute,
                                        params: {
                                            schoolSlug: school.urlSlug,
                                            part: part,
                                            courseAcronym: newRouteCourse(part)?.certificate.acronym.toLowerCase(),
                                            ratingAcronym: newRouteCourse(part)?.ratings[0]?.acronym.toLowerCase(),
                                        },
                                    }"
                                />
                            </div>
                        </div>
                        <div
                            v-if="school !== null && schoolStore.schools.length > 1"
                            class="flight-school-selection"
                        >
                            <q-separator class="q-mb-md" />
                            <FlightSchoolSelection
                                :current-school="school"
                                @select-school="(newSchool: FlightSchool) => schoolStore.setSchool(newSchool)"
                            />
                            <q-separator />
                        </div>
                    </template>
                </FsumNavMenu>
                <FsumNavButton
                    label="Student Search"
                    icon="o_person_search"
                    dense
                    :to="{ name: 'studentSearch' }"
                />
                <FsumNavButton
                    v-if="course !== null && course.certificate.part === 141"
                    label="Pass Rate Report"
                    icon="sym_o_lab_profile"
                    dense
                    :to="{ name: 'passRateReport' }"
                />
                <q-separator class="q-mb-md menu-separator" />
            </div>
            <div class="full-width course-selection">
                <q-scroll-area
                    :style="{ height: courseSelectionHeight }"
                    :thumb-style="{ width: '6px' }"
                >
                    <FlightSchoolCourseSelection
                        v-if="school !== null"
                        class="full-height"
                        :current-school="school"
                        :current-course="course"
                        @select-course="(newCourse: FlightSchoolCourse) => schoolStore.course = newCourse"
                        @set-school-courses="(school: FlightSchool, courses: FlightSchoolCourse[]) =>
                            schoolStore.setSchoolCourses(school, courses)"
                    />
                </q-scroll-area>
            </div>
        </div>
    </div>
</template>
<style scoped>
.menu-separator {
    margin-inline: -20px;
}
</style>
